import React, { createContext, useState, useEffect } from "react"
import {
  fetchCompanyProfile,
  getStoredLoginUser,
  invalidateCompanyProfile,
} from "../service/authService"

export const AuthContext = createContext()

const buildContextUser = (userData) => {
  if (!userData) return null
  let fullName = userData.fullName || userData.name || ""
  if (!fullName && userData.first_name && userData.last_name) {
    fullName = `${userData.first_name} ${userData.last_name}`
  }
  return { ...userData, fullName }
}

const AuthContextProvider = ({ children }) => {
  const hasToken = !!localStorage.getItem("token")
  const [user, setUser] = useState(() => buildContextUser(getStoredLoginUser()))
  const [isAuthenticated, setIsAuthenticated] = useState(hasToken)
  const [loading, setLoading] = useState(hasToken)

  const applyProfileResponse = (response) => {
    const userData = response?.data ?? response
    setUser(buildContextUser(userData))
    setIsAuthenticated(true)
    localStorage.setItem("isLoggedIn", "true")
  }

  const getUser = async () => {
    const token = localStorage.getItem("token")

    if (!token) {
      setIsAuthenticated(false)
      setUser(null)
      setLoading(false)
      return
    }

    try {
      const response = await fetchCompanyProfile()
      applyProfileResponse(response)
    } catch (e) {
      console.error("User authentication failed", e)
      if (e?.response?.status === 401) {
        invalidateCompanyProfile()
        localStorage.removeItem("token")
        localStorage.removeItem("isLoggedIn")
        localStorage.removeItem("userData")
        localStorage.removeItem("userDataLast")
        window.dispatchEvent(new Event("auth:changed"))
      }
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hasToken) getUser()
    else setLoading(false)
  }, [])

  const login = (token, userData) => {
    localStorage.setItem("token", token)
    setIsAuthenticated(true)
    setUser(buildContextUser(userData))
    setLoading(false)
    fetchCompanyProfile({ force: true }).then(applyProfileResponse).catch(() => {})
  }

  const logout = () => {
    invalidateCompanyProfile()
    localStorage.removeItem("token")
    localStorage.removeItem("isLoggedIn")
    setIsAuthenticated(false)
    setUser(null)
    window.dispatchEvent(new Event("auth:changed"))
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        setIsAuthenticated,
        login,
        logout,
        loading,
        refreshProfile: () => fetchCompanyProfile({ force: true }).then(applyProfileResponse),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContextProvider
